import {
  canonicalManagedConnectorId,
  MANAGED_AI_CONNECTOR_ID,
} from "./aiProviders/managedAiConnector";
import {
  canonicalPricingModelId,
  DEFAULT_MODEL_PRICING,
  MANAGED_MODEL_PRICING_ESTIMATES,
} from "./aiModelPricing";

export type UsagePriceRow = {
  spaceId: string | null;
  connectorId: string | null;
  measure: string;
  dimension: string | null;
  amountPerUnit: number;
};

const MEASURE_PRICING_FIELD = {
  input_tokens: "inputPerMillion",
  output_tokens: "outputPerMillion",
  cache_read_tokens: "cacheReadPerMillion",
  cache_write_tokens: "cacheWritePerMillion",
} as const;

// List price per single metered token for a known model, used as a fallback
// when no rate has been entered.
export function defaultPricePerUnit(
  measure: string,
  model: string
): number | null {
  const field =
    MEASURE_PRICING_FIELD[measure as keyof typeof MEASURE_PRICING_FIELD];
  if (!field) return null;
  const pricing =
    DEFAULT_MODEL_PRICING[model] ??
    DEFAULT_MODEL_PRICING[canonicalPricingModelId(model)];
  const perMillion = pricing?.[field];
  return perMillion == null ? null : perMillion / 1_000_000;
}

export function priceKey(
  connectorId: string,
  measure: string,
  dimension: string
) {
  return `${connectorId}\0${measure}\0${dimension}`;
}

// The managed provider has no connector_usage_prices rows, so its sentinel
// spend is priced from the published list-price estimates (per-1M → per-unit).
export function managedAiDefaultPrices(): Map<string, number> {
  const map = new Map<string, number>();
  for (const [model, pricing] of Object.entries(
    MANAGED_MODEL_PRICING_ESTIMATES
  )) {
    const set = (measure: string, perMillion: number | undefined) => {
      if (perMillion == null) return;
      map.set(
        priceKey(MANAGED_AI_CONNECTOR_ID, measure, model),
        perMillion / 1_000_000
      );
    };
    set("input_tokens", pricing.inputPerMillion);
    set("output_tokens", pricing.outputPerMillion);
    set("cache_read_tokens", pricing.cacheReadPerMillion);
    set("cache_write_tokens", pricing.cacheWritePerMillion);
  }
  return map;
}

// Rows pinned to a space or AI provider are ignored — connector spend prices
// the connector-wide total.
export function buildUsagePriceLookup(
  rows: readonly UsagePriceRow[],
  defaults?: ReadonlyMap<string, number>
): Map<string, number> {
  const map = new Map<string, number>(defaults);
  for (const row of rows) {
    if (row.connectorId == null) continue;
    if (row.spaceId != null) continue;
    map.set(
      priceKey(row.connectorId, row.measure, row.dimension ?? ""),
      row.amountPerUnit
    );
  }
  return map;
}

export function priceFor(
  prices: ReadonlyMap<string, number>,
  connectorId: string,
  measure: string,
  dimension: string
): number | null {
  return (
    prices.get(priceKey(connectorId, measure, dimension)) ??
    defaultPricePerUnit(measure, dimension) ??
    null
  );
}

// input_tokens is the grand total that already includes cache reads and writes,
// so only the fresh remainder is charged the input rate or cached tokens bill
// twice.
export function estimateTokenCostUsd(
  connectorId: string,
  model: string,
  tokens: {
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
  },
  prices: ReadonlyMap<string, number>
): number | null {
  const id = canonicalManagedConnectorId(connectorId);
  const inputPrice = priceFor(prices, id, "input_tokens", model);
  const outputPrice = priceFor(prices, id, "output_tokens", model);
  const cacheReadPrice = priceFor(prices, id, "cache_read_tokens", model);
  const cacheWritePrice = priceFor(prices, id, "cache_write_tokens", model);
  if (
    inputPrice == null &&
    outputPrice == null &&
    cacheReadPrice == null &&
    cacheWritePrice == null
  ) {
    return null;
  }
  const freshInput = Math.max(
    0,
    tokens.inputTokens - tokens.cacheReadTokens - tokens.cacheWriteTokens
  );
  return (
    freshInput * (inputPrice ?? 0) +
    tokens.outputTokens * (outputPrice ?? 0) +
    tokens.cacheReadTokens * (cacheReadPrice ?? 0) +
    tokens.cacheWriteTokens * (cacheWritePrice ?? 0)
  );
}
